import {
  ApiError,
  Button,
  ButtonColor,
  CategorizationTag,
  CategorizationTagColor,
  disabledQuery,
  FormError,
  IconName,
  Modal,
  ModalBody,
  ModalContent,
  ModalContentSize,
  ModalFooter,
  ModalHeader,
  ModalTitle,
  optionWithAsideProps,
  SelectControl,
  stringCompare,
  Table,
  TBody,
  TR,
  useCloseModal,
  useSetApiErrors,
  useToastManager
} from "@quortex/front-common"
import { type MakeGenerics, useSearch } from "@tanstack/react-location"
import { useMutation, useSuspenseQuery } from "@tanstack/react-query"
import { memo, useMemo, useState } from "react"
import { Controller, FormProvider, useForm } from "react-hook-form"
import { getInputTags, getOttEndpointOptions } from "src/App/play/helpers/ottendpoints"
import { useInput } from "src/queries/playInputs"
import { useAddOttEndpoint, useOttEndpointList } from "src/queries/playOttEndpoints"
import { usePool } from "src/queries/playPools"
import ArrowTR from "@/App/play/components/features/endpoints/ArrowTR"
import EndpointPanelTD from "@/App/play/components/features/endpoints/EndpointPanelTD"
import NameTD from "@/App/play/components/features/endpoints/NameTD"
import Identifier from "@/App/play/components/features/endpoints/ottEndpoints/Identifier"
import PlayPathComposition from "@/App/play/components/features/endpoints/ottEndpoints/PlayPathComposition"
import PanelTD from "@/App/play/components/features/endpoints/PanelTD"
import { useProcessingList } from "@/queries/playProcessings"
import { useTargetList } from "@/queries/playTargets"

export type AddOttEndpointModalGenerics = MakeGenerics<{
  Search: { modal: { name: "add_ott_endpoint"; inputUuid: string } }
}>

type Form = {
  processingUuid?: string
  targetUuid?: string
}

const AddOttEndpointModal = () => {
  const search = useSearch<AddOttEndpointModalGenerics>()
  const closeModal = useCloseModal()

  return (
    <Modal show={search.modal?.name === "add_ott_endpoint"} onDismiss={closeModal}>
      <Content />
    </Modal>
  )
}

// The `Modal` component renders its content only when it's open (`show=true`).
// To avoid triggering API request when modals are not displayed, the modal content is isolated
// in a separated component.

const Content = () => {
  const closeModal = useCloseModal()
  const search = useSearch<AddOttEndpointModalGenerics>()
  const [inputUuid] = useState(search.modal?.inputUuid)

  const useInputQuery = useInput({ inputUuid })
  const useIngestInputQuery = useInput({ inputUuid })
  const { data: localInput } = useSuspenseQuery(
    search.modal?.inputUuid && !search.modal?.inputUuid.startsWith("in_")
      ? useInputQuery
      : disabledQuery
  )
  const { data: ingestInput } = useSuspenseQuery(
    search.modal?.inputUuid.startsWith("in_") ? useIngestInputQuery : disabledQuery
  )

  const input = useMemo(() => localInput || ingestInput, [ingestInput, localInput])

  const { data: pool } = useSuspenseQuery(usePool())
  const { data: ottEndpoints } = useSuspenseQuery({
    ...useOttEndpointList(),
    select: d => d.results
  })
  const { data: processingList } = useSuspenseQuery({
    ...useProcessingList(),
    select: data => data.results
  })
  const { data: targetList } = useSuspenseQuery({
    ...useTargetList(),
    select: data => data.results
  })

  const [isLoading, setIsLoading] = useState(false)
  const { mutateAsync: addOttEndpoint } = useMutation(useAddOttEndpoint())
  const toastManager = useToastManager()

  //
  // We need to sort the processings and targets to display them in a consistent order
  //
  const sortedProcessings = useMemo(
    () => processingList.toSorted((a, b) => stringCompare(a.name, b.name)),
    [processingList]
  )
  const sortedTargets = useMemo(
    () => targetList.toSorted((a, b) => stringCompare(a.name, b.name)),
    [targetList]
  )

  //
  // OTT endpoints options are naturally sorted by processing and target
  //
  const ottEndpointOptions = useMemo(
    () => getOttEndpointOptions(ottEndpoints, inputUuid ?? "", sortedProcessings, sortedTargets),
    [inputUuid, ottEndpoints, sortedProcessings, sortedTargets]
  )

  //
  // Form is initialized with the first OTT endpoint option
  //
  const form = useForm<Form>({ defaultValues: ottEndpointOptions[0] })
  const setApiErrors = useSetApiErrors(form)
  const processingUuid = form.watch("processingUuid")
  const targetUuid = form.watch("targetUuid")

  //
  // Processing options are extracted from ottEndpointOptions and get naturally sorted
  // Same goes for targets, but targets are also filtered by the selected processing
  //
  const processingOptions = useMemo(
    () =>
      sortedProcessings
        .filter(p => ottEndpointOptions.some(o => o.processingUuid === p.uuid))
        .map(p => ({
          aside: (
            <Identifier className="ml-auto" color="purple">
              {p.identifier}
            </Identifier>
          ),
          label: p.name,
          value: p.uuid
        })),
    [ottEndpointOptions, sortedProcessings]
  )

  const targetOptions = useMemo(
    () =>
      sortedTargets
        .filter(t =>
          ottEndpointOptions.some(
            o => o.targetUuid === t.uuid && o.processingUuid === processingUuid
          )
        )
        .map(t => ({
          aside: (
            <Identifier className="ml-auto" color="teal">
              {t.identifier}
            </Identifier>
          ),
          label: t.name,
          value: t.uuid
        })),
    [ottEndpointOptions, processingUuid, sortedTargets]
  )

  //
  // We need to retrieve the processing and target objects to display their identifiers
  //
  const processing = useMemo(
    () => processingList.find(processing => processing.uuid === processingUuid),
    [processingUuid, processingList]
  )

  const target = useMemo(
    () => targetList.find(target => target.uuid === targetUuid),
    [targetUuid, targetList]
  )
  //
  // Callbacks
  //
  const onSubmit = form.handleSubmit(async data => {
    const { processingUuid, targetUuid } = data

    if (!inputUuid || !processingUuid || !targetUuid) {
      throw new Error("inputUuid, processingUuid and targetUuid are required")
    }

    try {
      setIsLoading(true)
      await addOttEndpoint({ enabled: true, inputUuid, processingUuid, targetUuid })
      closeModal()
    } catch (e) {
      if (e instanceof ApiError && e.status >= 400 && e.status < 500) {
        setApiErrors(e, data)
      } else {
        toastManager.error({ apiError: e })
      }
      setIsLoading(false)
    }
  })

  const updateTargetUuid = (processingUuid: string) => {
    const filteredOptions = ottEndpointOptions.filter(o => o.processingUuid === processingUuid)
    if (!filteredOptions.find(o => o.targetUuid === targetUuid)) {
      form.setValue("targetUuid", filteredOptions[0]?.targetUuid)
    }
  }

  return (
    <ModalContent size={ModalContentSize.S}>
      <form noValidate onSubmit={onSubmit}>
        <FormProvider {...form}>
          <ModalHeader onClose={closeModal}>
            <ModalTitle>Add an OTT endpoint</ModalTitle>
          </ModalHeader>
          <ModalBody>
            <div className="mt-md" id="dialog-description">
              <div className="radius-xs overflow-hidden overflow-x-auto border border-grey-300 bg-grey-100 p-lg">
                <Table className="w-full min-w-0 border-0!">
                  <TBody className="divide-y-0 bg-grey-100!">
                    <TR>
                      <NameTD icon={IconName.VIDEO} name="Input" />

                      <EndpointPanelTD
                        aside={<Identifier color="pink">{input?.identifier}</Identifier>}
                        description={
                          <div className="space-x-2xs" data-testid="input-tags">
                            {getInputTags(input).map(({ label, ...tag }) => (
                              <CategorizationTag
                                key={label}
                                color={CategorizationTagColor.OUTLINE}
                                {...tag}
                              >
                                {label}
                              </CategorizationTag>
                            ))}
                          </div>
                        }
                        name={input?.name}
                      />
                    </TR>

                    <ArrowTR />

                    <TR>
                      <NameTD icon={IconName.PROCESSING} name="Processing" />

                      <PanelTD>
                        <Controller
                          control={form.control}
                          name="processingUuid"
                          render={({ field }) => (
                            <div className="w-full" data-testid="select-processing">
                              <SelectControl
                                {...field}
                                className="w-full"
                                fixedMenu
                                isSearchable={false}
                                options={processingOptions}
                                onChange={processingUuid => {
                                  field.onChange(processingUuid)
                                  updateTargetUuid(processingUuid as string)
                                }}
                                {...optionWithAsideProps}
                              />
                            </div>
                          )}
                        />
                      </PanelTD>
                    </TR>

                    <ArrowTR />

                    <TR>
                      <NameTD icon={IconName.PACKAGING} name="Packaging" />
                      <PanelTD>
                        <Controller
                          control={form.control}
                          name="targetUuid"
                          render={({ field }) => (
                            <div data-testid="select-packaging">
                              <SelectControl
                                {...field}
                                className="w-full"
                                fixedMenu
                                isSearchable={false}
                                options={targetOptions}
                                {...optionWithAsideProps}
                              />
                            </div>
                          )}
                        />
                      </PanelTD>
                    </TR>

                    <ArrowTR />

                    <TR>
                      <NameTD icon={IconName.PACKAGING} name="Path" />
                      <PanelTD fitContent>
                        <PlayPathComposition
                          data-testid="ott-path"
                          endpoint={{ input, processing, target }}
                          processingType={pool.type}
                        />
                      </PanelTD>
                    </TR>
                  </TBody>
                </Table>
              </div>
            </div>
          </ModalBody>
          <FormError className="mt-md" />

          <ModalFooter className="mt-lg">
            <Button
              color={ButtonColor.SECONDARY}
              data-testid="cancel-btn"
              type="button"
              onClick={() => closeModal()}
            >
              Cancel
            </Button>
            <Button
              color={ButtonColor.PRIMARY}
              data-testid="create-btn"
              isLoading={isLoading}
              type="submit"
            >
              Create
            </Button>
          </ModalFooter>
        </FormProvider>
      </form>
    </ModalContent>
  )
}

export default memo(AddOttEndpointModal)
